﻿import { mutations } from "@/graphql";
import { useMutation } from "@apollo/client/react";
import { enqueueSnackbar } from "notistack";
import { useCallback, useContext } from "react";
import { useTextDomainContext } from "../../app/providers";
import type { MutationRemoveCompanyFromTenantArgs } from "@/types/graphql";

/**
 * Custom hook to delete a user company.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @returns {{deleteUserCompany: Function, deleting: boolean}} - The function to delete a user company and the loading state.
 */
export function useRemoveCompanyFromTenant(
  onCompleted: (data?: any) => void = () => {},
  onError: (error?: any) => void = () => {},
) {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const [removeCompanyFromTenant, { loading }] = useMutation<any>(
    mutations.removeCompanyFromTenant,
    {
      onCompleted: (response) => {
        if (response?.removeCompanyFromTenant?.success) {
          enqueueSnackbar(gettext("The company has been unlinked."), {
            variant: "success",
          });
        } else {
          enqueueSnackbar(gettext("Failed to unlink. ") + response.message, {
            variant: "error",
          });
        }
        onCompleted(response);
      },
      onError: (err) => {
        enqueueSnackbar("Error while unlink the company: " + err.message, {
          variant: "error",
        });
        onError(err);
      },
      errorPolicy: "all",
      refetchQueries: ["TenantsCompaniesList", "TenantsList"],
    },
  );

  const removeCompanyFromTenantRow = useCallback(
    (variables: MutationRemoveCompanyFromTenantArgs) => {
      removeCompanyFromTenant({
        variables,
      }).then();
    },
    [removeCompanyFromTenant],
  );

  return {
    removeCompanyFromTenant: removeCompanyFromTenantRow,
    deleting: loading,
  };
}
